import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../store";
import EndorsementService from "../../services/endorsement.service";
import { PaginationParamsType } from "../../types/common";

export const getEndorsementsFromApi: any = createAsyncThunk(
  "endorsements/getEndorsements",
  async (params: PaginationParamsType) => {
    if (!params) params = { page: 1, per_page: 1000, query: "" };
    const res: any = await EndorsementService.find(params);
    if (res?.response?.status >= 400) {
      throw new Error(res?.response?.data?.error);
    }
    return res || [];
  }
);

export const getApprovedEndorsementsFromApi: any = createAsyncThunk(
  "endorsements/getApprovedEndorsements",
  async () => {
    const res: any = await EndorsementService.findApproved();
    if (res?.response?.status >= 400) {
      throw new Error(res?.response?.data?.error);
    }
    return res || [];
  }
);

const initialState: any = {
  endorsements: [],
  totalCount: 0,
  totalPages: 0,
  templateText: "",
  entryDate: "",
  expiryDate: "",
  status: "idle",
  error: null,
};

export const endorsementsSlice = createSlice({
  name: "endorsement",
  initialState,
  reducers: {
    setTemplateText: (state, action: PayloadAction<any>) => {
      state.templateText = action.payload;
    },
    setEndorsementEntryData: (state, action: PayloadAction<any>) => {
      state.entryDate = action.payload;
    },
    setEndorsementExpiryData: (state, action: PayloadAction<any>) => {
      state.expiryDate = action.payload;
    },
    resetEndorsementValues: (state, action: PayloadAction<any>) => {
      state.templateText = "";
      state.entryDate = "";
      state.expiryDate = "";
    },
    setEndorsements: (state, action: PayloadAction<any>) => {
      state.endorsements = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Get Endorsements List
    builder.addCase(getEndorsementsFromApi.pending, (state, action) => {
      state.status = "loading";
    });
    builder.addCase(getEndorsementsFromApi.fulfilled, (state, action) => {
      state.status = "succeeded";
      state.endorsements = Array.isArray(action.payload?.endorsements)
        ? [...state.endorsements, ...action.payload?.endorsements]
        : state.endorsements;
      state.totalCount = action.payload?.totalCount || 0;
      state.totalPages = action.payload?.totalPages || 0;
    });
    builder.addCase(getEndorsementsFromApi.rejected, (state, action) => {
      state.status = "failed";
      state.error = action?.error?.message || "Something went wrong";
    });

    // Get Approved Endorsements List
    builder.addCase(getApprovedEndorsementsFromApi.pending, (state, action) => {
      state.status = "loading";
    });
    builder.addCase(
      getApprovedEndorsementsFromApi.fulfilled,
      (state, action) => {
        state.status = "succeeded";
        state.endorsements = action.payload
      }
    );
    builder.addCase(
      getApprovedEndorsementsFromApi.rejected,
      (state, action) => {
        state.status = "failed";
        state.error = action?.error?.message || "Something went wrong";
      }
    );
  },
});

export const {
  setTemplateText,
  setEndorsementEntryData,
  setEndorsementExpiryData,
  resetEndorsementValues,
  setEndorsements,
} = endorsementsSlice.actions;

export const getTemplateText = (state: RootState) =>
  state.endorsement.templateText;
export const getEndorsementEntryData = (state: RootState) =>
  state.endorsement.entryDate;
export const getEndorsementExpiryData = (state: RootState) =>
  state.endorsement.expiryDate;

export const getEndorsements = (state: RootState) =>
  state.endorsement.endorsements;
export const getEndorsementsTotalCount = (state: RootState) =>
  state.endorsement.totalCount;
export const getEndorsementsTotalPages = (state: RootState) =>
  state.endorsement.totalPages;

export default endorsementsSlice.reducer;
